import { createRouter, createWebHistory } from "vue-router";
import { setupLayouts } from "virtual:generated-layouts";
import { routes } from "vue-router/auto-routes";
import type { Router } from "vue-router";
import type { useAuthStore } from "@/stores/authStore";

export function setupRouter(
  authStore: ReturnType<typeof useAuthStore>
): Router {
  const router = createRouter({
    history: createWebHistory(),
    routes: setupLayouts(routes),
  });

  router.beforeEach(async (to, from, next) => {
    console.group(`[ROUTER] Navigating to ${to.fullPath}`);

    document.title = (to.meta.title as string) || "Pay My Swift Benefits";

    await authStore.waitForInit();

    const isAdmin = await authStore.forceRefreshAdminStatus();
    const adminOnly = to.meta?.adminOnly === true;
    const adminBlocked = to.meta?.adminBlocked === true;

    if (to.path === "/error") {
      console.warn("⚠️ Navigating to error page - skipping auth checks");
      return next();
    }

    if (to.path === "/AccessDenied") {
      console.warn(
        "⚠️ Navigating to Access Denied page - skipping auth checks"
      );
      return next();
    }

    if (authStore.isInternalUser && !isAdmin) {
      console.warn("🚫 Internal, non-admin trying to access app");
      return next("/AccessDenied");
    } else if (adminOnly && !isAdmin) {
      console.warn("🚫 Non-admin trying to access admin-only route");
      return next("/");
    } else if (adminBlocked && isAdmin) {
      console.warn("🚧 Admin trying to access restricted route");
      return next({
        path: "/admin",
      });
    }

    console.groupEnd();
    next();
  });

  // Handle Vite dynamic import errors
  router.onError((err, to) => {
    const dynamicImportFailed = err?.message?.includes?.(
      "Failed to fetch dynamically imported module"
    );

    if (dynamicImportFailed) {
      if (!localStorage.getItem("vuetify:dynamic-reload")) {
        console.warn("🔄 Reloading to fix dynamic import issue");
        localStorage.setItem("vuetify:dynamic-reload", "true");
        location.assign(to.fullPath);
      } else {
        console.error("❌ Dynamic import error persists after reload", err);
      }
    } else {
      console.error("❌ Router error:", err);
    }
  });

  return router;
}
